import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { PageHeader } from '../components/Layout';
import {
  Drawer,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Select,
  TextInput,
  useToast,
} from '../components/ui';

interface Smtp {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  fromName: string;
  fromEmail: string;
  isEnabled: boolean;
  hasPassword: boolean;
}

/** Outgoing mail. Nothing that emails anybody works until this does. */
export function PlatformMailPage() {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();
  const [form, setForm] = useState<Partial<Smtp> & { password?: string }>({});
  const [testTo, setTestTo] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const smtp = useQuery({
    queryKey: ['smtp'],
    queryFn: () => api.get<Smtp>('/api/platform/settings/smtp'),
  });

  const value = <K extends keyof Smtp>(key: K): Smtp[K] =>
    (form[key] ?? smtp.data?.[key]) as Smtp[K];

  const save = useMutation({
    mutationFn: () =>
      api.put<Smtp>('/api/platform/settings/smtp', {
        host: value('host') ?? '',
        port: Number(value('port') ?? 587),
        secure: Boolean(value('secure')),
        username: value('username') ?? '',
        // Blank keeps whatever is stored: this screen never receives it.
        password: form.password ?? '',
        fromName: value('fromName') ?? '',
        fromEmail: value('fromEmail') ?? '',
        isEnabled: Boolean(value('isEnabled')),
      }),
    onSuccess: () => {
      push('Mail settings saved', 'ok');
      setForm({});
      void queryClient.invalidateQueries({ queryKey: ['smtp'] });
    },
    onError: fail,
  });

  const test = useMutation({
    mutationFn: () =>
      api.post<{ sent: boolean; reason?: string }>('/api/platform/settings/smtp/test', {
        to: testTo.trim(),
      }),
    onSuccess: (res) => {
      setResult(res.sent ? `Sent to ${testTo.trim()}` : `Not sent: ${res.reason ?? 'unknown'}`);
      if (res.sent) push('Test message sent', 'ok');
    },
    onError: fail,
  });

  if (smtp.isLoading) return <Loading />;
  if (smtp.error) return <ErrorState error={smtp.error} onRetry={() => void smtp.refetch()} />;

  const data = smtp.data!;

  return (
    <div className="stack">
      <PageHeader
        title="Outgoing mail"
        subtitle="Password resets and registration codes are sent from here"
      />

      <div className="kpis">
        <Kpi
          label="Status"
          value={data.isEnabled && data.host ? 'On' : 'Off'}
          foot={data.isEnabled && data.host ? data.host : 'Nothing will be emailed'}
          tone={data.isEnabled && data.host ? '' : 'amber'}
        />
        <Kpi label="From" value={data.fromEmail || '—'} foot={data.fromName || 'No display name'} />
        <Kpi
          label="Password"
          value={data.hasPassword ? 'Stored' : 'Not set'}
          foot="Encrypted; never shown again"
          tone={data.hasPassword ? '' : 'amber'}
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>SMTP server</h2>
        </div>
        <div className="card-body col gap-16">
          <div className="form-grid">
            <TextInput
              label="Host"
              placeholder="smtp.zoho.in"
              value={value('host') ?? ''}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
            />
            <TextInput
              label="Port"
              type="number"
              hint="587 for STARTTLS, 465 for TLS"
              value={value('port') ?? 587}
              onChange={(e) => setForm({ ...form, port: Number(e.target.value) || 587 })}
            />
            <Select
              label="Encryption"
              value={value('secure') ? 'tls' : 'starttls'}
              onChange={(e) => setForm({ ...form, secure: e.target.value === 'tls' })}
            >
              <option value="starttls">STARTTLS (port 587)</option>
              <option value="tls">TLS (port 465)</option>
            </Select>
            <TextInput
              label="Username"
              value={value('username') ?? ''}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
            <TextInput
              label="Password"
              type="password"
              autoComplete="new-password"
              hint={data.hasPassword ? 'Leave blank to keep the stored one' : 'Required to send'}
              value={form.password ?? ''}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <TextInput
              label="From name"
              value={value('fromName') ?? ''}
              onChange={(e) => setForm({ ...form, fromName: e.target.value })}
            />
            <TextInput
              label="From address"
              type="email"
              value={value('fromEmail') ?? ''}
              onChange={(e) => setForm({ ...form, fromEmail: e.target.value })}
            />
          </div>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={Boolean(value('isEnabled'))}
              onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
            />
            <span>Send mail using these settings</span>
          </label>

          <div className="row gap-6">
            <button className="btn primary" disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>

          <p className="small muted">
            Until this is on, password reset links are written to the server log instead of being
            emailed, and registration does not ask for a verification code.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Send a test</h2>
          <span className="small muted">Prove it before anybody depends on it</span>
        </div>
        <div className="card-body col gap-16">
          <div className="row gap-6 wrap" style={{ alignItems: 'flex-end' }}>
            <TextInput
              label="Send to"
              type="email"
              placeholder="you@yourcompany.in"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <button
              className="btn"
              disabled={!testTo.trim() || test.isPending}
              onClick={() => test.mutate()}
            >
              {test.isPending ? 'Sending…' : 'Send test'}
            </button>
          </div>
          {result ? <p className="small">{result}</p> : null}
        </div>
      </div>
    </div>
  );
}

/* ---------------- sign-in challenge ---------------- */

interface Recaptcha {
  siteKey: string;
  isEnabled: boolean;
  hasSecretKey: boolean;
  isActive: boolean;
}

/**
 * Google reCAPTCHA on the sign-in and registration screens.
 *
 * Keys live here rather than in the environment because they are bound to a
 * domain: moving the platform should not need a redeploy to change them. With
 * nothing saved, the built-in drawn challenge is used, so a server that cannot
 * reach Google still has a working sign-in screen.
 */
export function PlatformCaptchaPage() {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();
  const [form, setForm] = useState<{ siteKey?: string; secretKey?: string; isEnabled?: boolean }>(
    {},
  );

  const settings = useQuery({
    queryKey: ['recaptcha'],
    queryFn: () => api.get<Recaptcha>('/api/platform/settings/recaptcha'),
  });

  const save = useMutation({
    mutationFn: () =>
      api.put<Recaptcha>('/api/platform/settings/recaptcha', {
        siteKey: (form.siteKey ?? settings.data?.siteKey ?? '').trim(),
        isEnabled: form.isEnabled ?? settings.data?.isEnabled ?? false,
        // Blank means "keep the one you have": the screen never receives the
        // secret, so it has nothing to send back.
        ...(form.secretKey?.trim() ? { secretKey: form.secretKey.trim() } : {}),
      }),
    onSuccess: () => {
      push('Saved', 'ok');
      setForm({});
      void queryClient.invalidateQueries({ queryKey: ['recaptcha'] });
    },
    onError: fail,
  });

  if (settings.isLoading) return <Loading />;
  if (settings.error)
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;

  const data = settings.data!;
  const siteKey = form.siteKey ?? data.siteKey;
  const isEnabled = form.isEnabled ?? data.isEnabled;

  return (
    <div className="stack">
      <PageHeader
        title="Sign-in challenge"
        subtitle="Google reCAPTCHA on the sign-in and registration screens"
      />

      <div className="kpis">
        <Kpi
          label="In use"
          value={data.isActive ? 'reCAPTCHA' : 'Built-in'}
          foot={data.isActive ? 'Google checks every attempt' : 'The drawn challenge Kortex ships'}
          tone={data.isActive ? '' : 'amber'}
        />
        <Kpi
          label="Secret key"
          value={data.hasSecretKey ? 'Stored' : 'Not set'}
          foot="Encrypted; never shown again"
          tone={data.hasSecretKey ? '' : 'amber'}
        />
        <Kpi label="Site key" value={data.siteKey ? 'Set' : '—'} foot="Public, sent to browsers" />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>reCAPTCHA v2 keys</h2>
          <span className="chip">From google.com/recaptcha/admin</span>
        </div>
        <div className="card-body">
          <div className="form-grid two">
            <TextInput
              label="Site key"
              placeholder="6Lc…"
              hint="Public. Rendered into the sign-in page."
              value={siteKey}
              onChange={(e) => setForm({ ...form, siteKey: e.target.value })}
            />
            <TextInput
              label="Secret key"
              type="password"
              autoComplete="new-password"
              placeholder={data.hasSecretKey ? 'Stored — leave blank to keep it' : '6Lc…'}
              hint="Never leaves the server."
              value={form.secretKey ?? ''}
              onChange={(e) => setForm({ ...form, secretKey: e.target.value })}
            />
            <Select
              label="Use it"
              value={isEnabled ? 'yes' : 'no'}
              onChange={(e) => setForm({ ...form, isEnabled: e.target.value === 'yes' })}
            >
              <option value="yes">Yes — challenge every sign-in</option>
              <option value="no">No — use the built-in challenge</option>
            </Select>
          </div>

          <button
            className="btn primary"
            style={{ marginTop: 14 }}
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>

          <p className="small muted" style={{ marginTop: 12 }}>
            Choose <b>reCAPTCHA v2, “I’m not a robot”</b> when creating the keys, and list every
            domain the app is reached on. Until a site key and secret are saved and switched on,
            sign-in keeps using the challenge Kortex draws itself — which is also what a server
            with no route to Google should stay on.
          </p>
        </div>
      </div>
    </div>
  );
}

interface ErrorRow {
  id: string;
  status: number;
  code: string;
  message: string;
  path: string;
  method: string;
  ip: string | null;
  at: string;
  hasStack: boolean;
}

/** Server errors, without needing shell access to whichever box this runs on. */
export function PlatformErrorsPage() {
  const { push } = useToast();
  const [days, setDays] = useState(7);
  const [code, setCode] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const errors = useQuery({
    queryKey: ['platform-errors', days, code],
    queryFn: () =>
      api.get<{
        items: ErrorRow[];
        byCode: Array<{ code: string; count: number }>;
        since: string;
      }>(`/api/platform/errors?days=${days}${code ? `&code=${encodeURIComponent(code)}` : ''}`),
    refetchInterval: 30_000,
  });

  if (errors.isLoading) return <Loading label="Reading the log…" />;
  if (errors.error)
    return <ErrorState error={errors.error} onRetry={() => void errors.refetch()} />;

  const data = errors.data!;

  return (
    <div className="stack">
      <PageHeader
        title="Server errors"
        subtitle="Every 5xx the API raised, with the stack trace behind it"
        actions={
          <Select label="" value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
          </Select>
        }
      />

      <div className="kpis">
        <Kpi
          label="Errors"
          value={data.items.length}
          foot={`Since ${new Date(data.since).toLocaleDateString('en-IN')}`}
          tone={data.items.length ? 'red' : ''}
        />
        {data.byCode.slice(0, 3).map((row) => (
          <Kpi key={row.code} label={row.code} value={row.count} foot="occurrences" />
        ))}
      </div>

      <div className="card">
        <div className="card-head">
          <div className="tabs">
            <button className={`tab ${code === '' ? 'active' : ''}`} onClick={() => setCode('')}>
              Everything
            </button>
            {data.byCode.slice(0, 5).map((row) => (
              <button
                key={row.code}
                className={`tab ${code === row.code ? 'active' : ''}`}
                onClick={() => setCode(row.code)}
              >
                {row.code} · {row.count}
              </button>
            ))}
          </div>
          <span className="small muted">Refreshes every 30 seconds</span>
        </div>

        {data.items.length === 0 ? (
          <Empty message="Nothing has gone wrong in this window." />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Code</th>
                  <th>Message</th>
                  <th>Request</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} className="clickable" onClick={() => setOpenId(row.id)}>
                    <td className="xs">
                      {new Date(row.at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td>
                      <span className="chip danger">{row.code}</span>
                    </td>
                    <td className="small">{row.message}</td>
                    <td className="xs mono muted">
                      {row.method} {row.path}
                    </td>
                    <td className="xs">{row.hasStack ? 'Stack' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openId ? (
        <ErrorDrawer
          id={openId}
          onClose={() => setOpenId(null)}
          onCopied={() => push('Stack trace copied', 'ok')}
        />
      ) : null}
    </div>
  );
}

function ErrorDrawer({
  id,
  onClose,
  onCopied,
}: {
  id: string;
  onClose: () => void;
  onCopied: () => void;
}) {
  const detail = useQuery({
    queryKey: ['platform-errors', id],
    queryFn: () =>
      api.get<
        ErrorRow & { stack: string | null; userId: string | null; propertyId: string | null }
      >(`/api/platform/errors/${id}`),
  });
  const data = detail.data;

  const copy = async () => {
    if (!data) return;
    const text = [
      `${data.code} ${data.status} — ${data.message}`,
      `${data.method} ${data.path}`,
      `at ${new Date(data.at).toISOString()}`,
      '',
      data.stack ?? '(no stack trace)',
    ].join('\n');
    await navigator.clipboard.writeText(text);
    onCopied();
  };

  return (
    <Drawer title={data ? data.code : 'Error'} size="wide" onClose={onClose}>
      {detail.isLoading || !data ? (
        <Loading />
      ) : (
        <div className="stack">
          <div className="card">
            <div className="card-body col gap-6">
              <div className="b">{data.message}</div>
              <div className="small mono muted">
                {data.method} {data.path}
              </div>
              <div className="xs muted">
                {new Date(data.at).toLocaleString('en-IN')} · status {data.status}
                {data.ip ? ` · ${data.ip}` : ''}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Stack trace</h2>
              <button className="btn sm" onClick={() => void copy()} disabled={!data.stack}>
                Copy
              </button>
            </div>
            <div className="card-body">
              {data.stack ? (
                <pre className="stack-trace">{data.stack}</pre>
              ) : (
                <Empty message="This error carried no stack trace." />
              )}
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}
